import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import sequencesService from '@services/sequences';
import customersService from '@services/customers';
import clientesService from '@services/clientes';
import productsService from '@services/products';
import postosService from '@services/postos';
import taxService from '@services/tax';
import facturasService from '@services/facturas';
import ccService from '@services/cc';
import ispService from '@services/isp';
import reducaoIspService from '@services/reducaoIsp';
import sobrecustoBiocombustivelService from '@services/sobrecustoBiocombustivel';
import {
  MdAddCircleOutline,
  MdClear,
  MdRefresh,
  MdPreview,
  MdTurnLeft,
  MdBusiness,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdReceipt,
  MdFingerprint,
  MdPerson,
} from "react-icons/md";
import AutoCompleteField from "@components/AutoCompleteV2";
import Input from "@components/Input";
import Switch from "@components/Switch";
import Menu from "@components/Menu";
import PDFPrint from "@components/PdfPrint";
import Alert from "@mui/material/Alert";
import TextField from "@components/TextField";
import TextArea from "@components/TextArea";
import Vencimento from '@components/Vencimento';
import Modal from '@components/Modal';
import AtLogin from '@components/AtLogin';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import UserContext from "@components/context/userContext";

import ClipLoader from "react-spinners/ClipLoader";

import { EventBus as eventBus } from "@components/EventBus";
import { showGlobalLoading, hideGlobalLoading } from "@services/axiosInstance";

import { currentDateTime, currentDate, dateToTimestamp } from '@utils/date'

import { calculateBestCondition } from '@utils/price';
import { valueWithoutIva, valueWithIva, threeDecimal } from "@utils/format.js";
import { validateNIF } from '@utils/nif';

import "./form.css";
import TestEnvironmentAlert from "@components/TestEnvironmentAlert";
const doctypesTitles = (doctype) => {
  const titles = {
    'facturas': 'Nova Fatura',
    'vendas_dinheiro': 'Nova Fatura/Recibo',
    'recibos': 'Novo Recibo',
    'notas_credito': 'Nova Nota de Crédito',
    'orcamento': 'Novo Orçamento',
    'guias_transporte': 'Nova Guia de Transporte',
  };
  return titles[doctype] || 'Novo Documento';
}

const mapSeriesDoctype = (doctype) => {
  const seriesMap = {
    'facturas': 'FT',
    'vendas_dinheiro': 'FR',
    'recibos': 'RG',
    'notas_credito': 'NC',
    'orcamento': 'OR',
    'guias_transporte': 'GT',
  };
  return seriesMap[doctype] || '';
};

const roundToTwo = (num) => {
    return +(Math.round(num + "e+2")  + "e-2");
}

function App() {
  const profile = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const { doctype } = useParams();

  if (
    ![
      'facturas',
      'vendas_dinheiro',
      'recibos',
      'guias_transporte',
      'notas_credito',
      // 'orcamento',
    ].includes(doctype)) {
      history.push("/sales/new");
    }

  const defaultCustomer = ['vendas_dinheiro'].includes(doctype) ? {
    idcustomer: "CF",
    name: "Consumidor Final",
    code: "CF",
  } : {};

  const [isSubmiting, setIsSubmiting] = useState(false);

  const [selectedPageSize, setSelectedPageSize] = useState('a4');
  const [selectedPageType, setSelectedPageType] = useState('1');

  const [customers, setCustomers] = useState([defaultCustomer]);
  const [products, setProducts] = useState([]);
  const [series, setSeries] = useState({
    FT: '',
    FR: '',
    RG: '',
    GT: '',
    NC: '',
  });
  const [recurrence, setRecurrence] = useState({
    isRecurring: false,
    frequency: 'monthly',
    interval: 1,
    startDate: new Date().toISOString().split('T')[0],
    withEndDate: false,
    endDate: ''
  });
  const [postos, setPostos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [taxa_ivas, settaxa_ivas] = useState([]);
  const [isps, setIsps] = useState([]);
  const [redIsp, setRedIsp] = useState([]);
  const [sc, setSc] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [selectedMatricula, setSelectedMatricula] = useState({ id: 'custom', matricula: 'Customizada' });
  const [isCustomMatricula, setIsCustomMatricula] = useState(true);
  const [atLogin, setAtLogin] = useState({ username: '' });
  const [isAtLoginValid, setIsAtLoginValid] = useState(false);
  const [error, setError] = useState(null);
  const [previewId] = useState(crypto.randomUUID());
  const [previewUrl, setPreviewUrl] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    lines: {},
    customer: false,
    nif: false,
    date: false,
    data_vencimento: false,
    paymentMethod: false,
  });

  // Inicializar o ref com null - será atualizado no useEffect
  const prefillReciboRef = useRef(null);
  // Flag para indicar se o prefill já foi processado
  const prefillProcessedRef = useRef(false);
  
  // Atualizar prefillReciboRef quando location.state mudar ou quando o componente for montado
  useEffect(() => {
    // Se há prefillRecibo no location.state e estamos em recibos, atualizar o ref
    if (location.state?.prefillRecibo && doctype === 'recibos') {
      prefillReciboRef.current = location.state.prefillRecibo;
      prefillProcessedRef.current = false; // Resetar flag quando há novo prefill
    }
    // Se não há prefillRecibo no state mas o ref já tem um valor, manter o valor do ref
    // Isto permite que o ref mantenha o valor mesmo se o state for limpo depois
  }, [location.state, doctype]);

  // Estado para controlar qual campo está sendo editado
  const [focusedField, setFocusedField] = useState(null);
  
  // Estado para controlar valores temporários durante edição
  const [tempValues, setTempValues] = useState({});

  // Estado para controlar observações do cabeçalho
  const [headerObs, setHeaderObs] = useState('');

  // Função de arredondamento inteligente (máximo 3 casas decimais, remove zeros à direita)
  const smartRound = (value, maxDecimals = 3) => {
    // Verificar se o valor é válido e numérico
    if (value === null || value === undefined || value === '' || isNaN(value)) return 0;
    
    // Converter para número se for string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // Verificar se é um número válido
    if (isNaN(numValue) || numValue === 0) return 0;
    
    const rounded = parseFloat(numValue.toFixed(maxDecimals));
    return parseFloat(rounded.toString()); // Remove zeros à direita
  };

  // Funções auxiliares para cálculos
  const calculateLineValues = (precoSemIva, taxaIva, descontoPer, valorDesconto, quantidade = 1) => {
    const precoComIva = precoSemIva > 0 ? smartRound(valueWithIva(precoSemIva, taxaIva), 6) : 0;
    const subtotal = smartRound(quantidade * precoSemIva, 6);
    const descontoCalculado = descontoPer > 0 ? smartRound((subtotal * descontoPer) / 100, 6) : smartRound(valorDesconto, 6);
    const afterDiscount = smartRound(subtotal - descontoCalculado, 6);
    const taxAmount = smartRound(afterDiscount * (taxaIva / 100), 6);
    const total = smartRound(afterDiscount + taxAmount, 6);
    
    return {
      preco_sem_iva: smartRound(precoSemIva, 6),
      preco_com_iva: precoComIva,
      desconto_per: smartRound(descontoPer, 6),
      valor_desconto: descontoCalculado,
      subtotal: subtotal,
      taxAmount: taxAmount,
      total: total
    };
  };

  // Função auxiliar para executar lógica do blur
  const executeBlurLogic = (index, fieldName, tempValue, line) => {
    setFocusedField(null);
    const finalValue = tempValue === '' ? 0 : Number(tempValue);
    
    // Atualizar linha com novos valores calculados
    const newLineValues = updateLineField(index, fieldName, finalValue, line);
    updateLine(index, newLineValues);
    
    // Limpar valor temporário
    setTempValues(prev => {
      const newTemp = { ...prev };
      delete newTemp[`${fieldName}-${index}`];
      return newTemp;
    });
  };

  // Handlers genéricos para campos numéricos
  const createFieldHandlers = (index, fieldName, line) => {
    const fieldKey = `${fieldName}-${index}`;
    
    return {
      value: focusedField === fieldKey 
        ? (tempValues[fieldKey] !== undefined ? tempValues[fieldKey] : '')
        : (line[fieldName] === 0 ? '' : line[fieldName] || ''),
      
      onFocus: () => {
        setFocusedField(fieldKey);
        setTempValues(prev => ({
          ...prev,
          [fieldKey]: line[fieldName] === 0 ? '' : line[fieldName] || ''
        }));
      },
      
      onBlur: () => {
        const tempValue = tempValues[fieldKey];
        executeBlurLogic(index, fieldName, tempValue, line);
      },
      
      onKeyDown: (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          // Chamar o método blur() nativo do input
          e.target.blur();
        }
      },
      
      onChange: (e) => {
        e.persist();
        setFocusedField(fieldKey);
        setTempValues(prev => ({
          ...prev,
          [fieldKey]: e?.target?.value || ''
        }));
      }
    };
  };

  const updateLineField = (index, fieldName, value, line) => {
    const quantidade = line.quantidade || 1;
    const taxaIva = line.taxa_iva || 0;
    
    // Converter valor para número e validar
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    const validValue = isNaN(numValue) ? 0 : numValue;
    
    let newValues = { ...line };
    
    switch (fieldName) {
      case 'preco_sem_iva':
        newValues.preco_sem_iva = smartRound(validValue);
        const calculated = calculateLineValues(validValue, taxaIva, line.desconto_per || 0, line.valor_desconto || 0, quantidade);
        newValues = { ...newValues, ...calculated };
        break;
        
      case 'preco_com_iva':
        newValues.preco_com_iva = smartRound(validValue);
        const precoSemIva = validValue > 0 ? valueWithoutIva(validValue, taxaIva) : 0;
        const calculatedFromComIva = calculateLineValues(precoSemIva, taxaIva, line.desconto_per || 0, line.valor_desconto || 0, quantidade);
        newValues = { ...newValues, ...calculatedFromComIva };
        break;
        
      case 'desconto_per':
        newValues.desconto_per = smartRound(validValue);
        const calculatedFromPer = calculateLineValues(line.preco_sem_iva || 0, taxaIva, validValue, 0, quantidade);
        newValues = { ...newValues, ...calculatedFromPer };
        break;
        
      case 'valor_desconto':
        newValues.valor_desconto = smartRound(validValue);
        const subtotal = smartRound(quantidade * (line.preco_sem_iva || 0), 6);
        const descontoPer = subtotal > 0 ? smartRound((validValue / subtotal) * 100, 6) : 0;
        const calculatedFromValor = calculateLineValues(line.preco_sem_iva || 0, taxaIva, descontoPer, validValue, quantidade);
        newValues = { ...newValues, ...calculatedFromValor };
        break;
        
      default:
        // Não fazer nada para campos não reconhecidos
        break;
    }
    
    return newValues;
  };

  const latestCustomerRef = useRef(null);
  const latestProductRef = useRef(null);
  const latestPostoRef = useRef(null);
  const latestDocumentoRef = useRef(null);
  const latestValorRef = useRef(null);
  const printerRef = useRef(null);


  const payments = [].includes(doctype) ? [
    { value: 1, label: "Dinheiro" },
    { value: 2, label: "Crédito" },
    { value: 3, label: "Cheque" },
    { value: 4, label: "TPA" },
    { value: 8, label: "Frota" },
    { value: 14, label: "Serviços Internos" },
    { value: 104, label: "Transferência" },
  ] : [
    { value: 1, label: "Dinheiro" },
    { value: 3, label: "Cheque" },
    { value: 4, label: "TPA" },
    { value: 104, label: "Transferência" },
  ];

  const frequences = [
    { value: 'daily', label: "Diária" },
    { value: 'weekly', label: "Semanal" },
    { value: 'monthly', label: "Mensal" },
    { value: 'yearly', label: "Anual" },
  ];

  const emptyLine = {
    produto: "",
    postoId: -1,
    designacao: "",
    quantidade: 1,
    preco_sem_iva: 0,
    preco_com_iva: 0,
    desconto_per: 0,
    valor_desconto: 0,
    taxa_iva: 0,
    subtotal: 0,
    taxAmount: 0,
    total: 0,
    obs_ft: "",
  };

  const emptyLineGuias = {
    produto: "",
    designacao: "",
    quantidade: 1,
  };

  const emptyLineRecibos = {
    date_time: "",
    data_documento: "",
    tipo_documento: "",
    numero_documento: "",
    texto: "",
    valor: 0,
    valor_inicial: 0,
  };

  const [invoice, setInvoice] = useState({
    customerId: (doctype === 'recibos' && location.state?.prefillRecibo?.customerId) ? location.state.prefillRecibo.customerId : defaultCustomer.idcustomer,
    date: ['guias_transporte'].includes(doctype) ? currentDate() : currentDateTime(),
    data_vencimento: new Date(),
    paymentMethod: ['notas_credito'].includes(doctype) ? '' : ['vendas_dinheiro'].includes(doctype) ? 1 : ['facturas'].includes(doctype) ? 2 : 1,
    serie: '',
    nif: '',
    obs: '',
    local_carga: '',
    data_hora_carga: currentDateTime(),
    local_descarga: '',
    data_hora_descarga: '',
    matricula: '',
    motorista: '',
    lines: [],
    subtotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    total: 0,
    ivas: {},
  });

  const [selectedPostoCarga, setSelectedPostoCarga] = useState({ id: 'custom', nome: 'Customizada', address: '', postalcode: '', city: '' });
  const [isCustomCarga, setIsCustomCarga] = useState(true);

  // Sincronizar estado local com estado do invoice
  useEffect(() => {
    setHeaderObs(invoice.obs || '');
  }, [invoice.obs]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Mapear tipo de documento para o serviço de séries correspondente
        const seriesMapping = {
          'facturas': 'FT_epos',
          'vendas_dinheiro': 'FR_epos',
          'notas_credito': 'NC_epos',
          'recibos': 'RG_epos',
          'guias_transporte': 'GT_epos',
        };
        
        const seriesKey = seriesMapping[doctype];
        
        if (!seriesKey) {
          window.location.href = '/sales';
          return;
        }
        
        // Buscar apenas a série necessária para o tipo de documento atual
        const responseSeries = await sequencesService.get(profile.idEmpresa, seriesKey);
        
        if (!responseSeries || !responseSeries.length) {
          window.location.href = '/sales';
          return;
        }

        // Buscar todas as séries para preencher o estado
        const [responseSeriesFT, responseSeriesFR, responseSeriesRG, responseSeriesGT, responseSeriesNC] = await Promise.all([
          sequencesService.get(profile.idEmpresa, 'FT_epos').catch(() => []),
          sequencesService.get(profile.idEmpresa, 'FR_epos').catch(() => []),
          sequencesService.get(profile.idEmpresa, 'RG_epos').catch(() => []),
          sequencesService.get(profile.idEmpresa, 'GT_epos').catch(() => []),
          sequencesService.get(profile.idEmpresa, 'NC_epos').catch(() => []),
        ]);

        const [customersRes, productsRes, postosRes, taxa_ivasRes, ispRes, redIspRes, scRes] =
          await Promise.all([
            customersService.get(profile.idEmpresa),
            productsService.get(profile.idEmpresa),
            postosService.get(profile.idEmpresa),
            taxService.get(profile.idEmpresa),
            ispService.get(profile.idEmpresa),
            reducaoIspService.get(profile.idEmpresa),
            sobrecustoBiocombustivelService.get(profile.idEmpresa)
          ]);

        const allCustomers = ['vendas_dinheiro', 'notas_credito'].includes(doctype) ? [defaultCustomer, ...customersRes] : customersRes;
        setCustomers(allCustomers);

        const filteredProducts = productsRes.filter(
          (product) => product.idproduct !== ""
        );
        setProducts(filteredProducts);

        setPostos(postosRes);
        settaxa_ivas(taxa_ivasRes);
        setIsps(ispRes);
        setRedIsp(redIspRes);
        setSc(scRes);

        setSeries({
          FT: responseSeriesFT[0]?.sequence,
          FR: responseSeriesFR[0]?.sequence,
          RG: responseSeriesRG[0]?.sequence,
          GT: responseSeriesGT[0]?.sequence,
          NC: responseSeriesNC[0]?.sequence,
        })

        if (['facturas', 'recibos'].includes(doctype) && !invoice.customerId) {
          setTimeout(() => latestCustomerRef.current?.focus(), 100);
        }
      } catch (err) {
        setError("Erro ao carregar dados. Por favor, tente novamente.");
      }
    };

    fetchData();
  }, [profile.idEmpresa, doctype, invoice.customerId]);

  // Verificar se há linhas com IVA = 'ND' (apenas para facturas e vendas_dinheiro)
  const hasLinesWithNDIva = () => {
    // Para guias de transporte, não validar IVA
    if (['guias_transporte'].includes(doctype)) {
      return false;
    }
    return invoice.lines.some(line => 
      line.produto && (line.iva === null || line.iva === undefined || line.iva === '')
    );
  };

  const addLine = (first = false) => {
    if (!invoice.customerId) {
      setValidationErrors((prev) => ({ ...prev, customer: true }));
      return;
    }

    // Bloquear adição de novas linhas se houver linhas com IVA = 'ND'
    if (hasLinesWithNDIva()) {
      NotificationManager.error('Corrija as linhas com IVA não encontrado antes de adicionar novas linhas', 'Erro de Validação', 3000);
      return;
    }

    if (['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype)) {
      const previousLine = invoice.lines[invoice.lines.length - 1];
      const previousPosto = previousLine?.postoId || -1;
  
      setInvoice((prev) => ({
        ...prev,
        lines: [...prev.lines, { ...emptyLine, postoId: previousPosto }],
      }));
      setError(null);
  
      if (!first) {
        setTimeout(() => latestProductRef.current?.focus(), 100);
      } else {
        setTimeout(() => latestPostoRef.current?.focus(), 100);
      }
    } else if (['guias_transporte'].includes(doctype)) {
      setInvoice((prev) => ({
        ...prev,
        lines: [...prev.lines, { ...emptyLineGuias }],
      }));
      setError(null);
  
      if (!first) {
        setTimeout(() => latestProductRef.current?.focus(), 100);
      }
    } else if (['recibos'].includes(doctype)) {
      setInvoice((prev) => {
        const newLines = [...prev.lines, { ...emptyLineRecibos }];
        return calculateTotals(prev, newLines);
      });
      setError(null);

      
      setTimeout(() => latestDocumentoRef.current?.focus(), 100);
    }
  };

  const removeLine = (index) => {
    setInvoice((prev) => {
      const newLines = prev.lines.filter((_, i) => i !== index);
      return calculateTotals(prev, newLines);
    });
  };

  const calculateTotals = (prevInvoice, lines) => {
    let totals = {};
    if (['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype)) {
      totals = lines.reduce(
        (acc, line) => {
          const quantidade = line.quantidade === '' ? 0 : (line.quantidade || 0);
          const preco_sem_iva = line.preco_sem_iva === '' ? 0 : (line.preco_sem_iva || 0);
          const subtotal = smartRound(quantidade * preco_sem_iva, 6);
          const valor_desconto = smartRound(line.valor_desconto || 0, 6);
          const afterDiscount = smartRound(subtotal - valor_desconto, 6);
          const taxAmount = smartRound(afterDiscount * (parseFloat(line.taxa_iva || 0) / 100), 6);
          
          // Inicializar ivas por taxa se não existir
          if (!acc.ivas) {
            acc.ivas = {};
          }
          
          // Adicionar IVA por taxa
          const ivaTax = parseFloat(line.taxa_iva || 0);
          if (ivaTax > 0 && taxAmount > 0) {
            const taxKey = `${ivaTax}%`;
            if (!acc.ivas[taxKey]) {
              acc.ivas[taxKey] = 0;
            }
            acc.ivas[taxKey] = smartRound(acc.ivas[taxKey] + taxAmount, 2);
          }
  
          return {
            subtotal: acc.subtotal + subtotal,
            totalDiscount: acc.totalDiscount + valor_desconto,
            totalTax: acc.totalTax + taxAmount,
            total: acc.total + afterDiscount + taxAmount,
            ivas: acc.ivas,
          };
        },
        { subtotal: 0, totalDiscount: 0, totalTax: 0, total: 0, ivas: {} }
      );
    } else if (['recibos'].includes(doctype)) {
      totals = lines.reduce(
        (acc, line) => {
          let valor = smartRound(line.valor || 0);          
          if (line.tipo === 'notas_credito') {
            valor = -valor;
          }
          return {
            total: acc.total + valor,
          };
        },
        { total: 0 }
      );
    }

    return {
      ...prevInvoice,
      lines,
      ...totals,
    };
  };

  const preselectReceiptDocument = (documentsList, criteria = {}) => {
    if (!criteria || !documentsList?.length) {
      return;
    }

    // Melhorar a busca: tentar encontrar por doc primeiro (mais específico), depois por numero
    // Se ambos estiverem disponíveis, usar ambos para garantir correspondência exata
    const targetDoc = documentsList.find((doc) => {
      if (criteria.doc && criteria.numero) {
        // Se temos ambos, verificar ambos para correspondência exata
        const matchesDoc = criteria.doc.trim() === doc.doc?.trim();
        const matchesNumero = Number(criteria.numero) === Number(doc.numero);
        const matchesTipo = !criteria.tipo || criteria.tipo === doc.tipo;
        return matchesDoc && matchesNumero && matchesTipo;
      } else if (criteria.doc) {
        // Se só temos doc, verificar apenas doc
        return criteria.doc.trim() === doc.doc?.trim();
      } else if (criteria.numero) {
        // Se só temos numero, verificar apenas numero
        return Number(criteria.numero) === Number(doc.numero);
      }
      return false;
    });

    if (!targetDoc) {
      return;
    }

    setInvoice((prev) => {
      if (prev.lines.some((line) => line.doc === targetDoc.doc)) {
        return prev;
      }

      const newLines = [
        ...prev.lines,
        {
          ...emptyLineRecibos,
          doc: targetDoc.doc,
          numero_documento: Number(targetDoc.numero),
          date_time: Number(targetDoc.date_time),
          data_documento: targetDoc.data,
          tipo_documento: ['facturas'].includes(targetDoc.tipo) ? 'FT' : ['notas_credito'].includes(targetDoc.tipo) ? 'NC' : 'FA',
          tipo: targetDoc.tipo,
          valor_inicial: roundToTwo(targetDoc.valor_liquido),
          valor: roundToTwo(criteria.valor || targetDoc.diff),
          diff: Number(targetDoc.diff),
        }
      ];

      return calculateTotals(prev, newLines);
    });
  };

  const handleCustomerSelection = async (customer, options = {}) => {
    setValidationErrors((prev) => ({
      ...prev,
      customer: false,
    }));

    const selectedCustomerId = customer ? customer.idcustomer : defaultCustomer.idcustomer;

    setInvoice((prev) => ({
      ...prev,
      customerId: selectedCustomerId,
      lines: []
    }));

    setDocumentos([]);

    if (!customer) {
      setMatriculas([]);
      return;
    }

    try {
      const documentosRes = await ccService.get(profile.idEmpresa, {
        days: 0,
        customerId: customer.idcustomer
      });

      const filteredDocumentos = Object.keys(documentosRes?.customers?.[customer.idcustomer]?.items || {}).reduce((acc, docKey) => {
        acc.push(documentosRes.customers[customer.idcustomer].items[docKey]);
        return acc;
      }, []);

      const mappedDocs = filteredDocumentos.map((filteredDocumento) => ({
        ...filteredDocumento,
        valor_liquido: roundToTwo(filteredDocumento.valor_liquido),
      }));

      setDocumentos(mappedDocs || []);

      if (options.prefillDocument) {
        preselectReceiptDocument(mappedDocs, options.prefillDocument);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos do cliente:', error);
      setDocumentos([]);
    }

    if (customer.idcustomer !== 'CF') {
      try {
        const matriculasRes = await clientesService.getMatriculas(profile.idEmpresa, customer.idcustomer);
        const matriculasList = matriculasRes !== "NOK" ? matriculasRes : [];
        setMatriculas(matriculasList);
      } catch (error) {
        console.error('Erro ao buscar matrículas:', error);
        setMatriculas([]);
      }
    } else {
      setMatriculas([]);
    }
  };

  useEffect(() => {
    if (doctype !== 'recibos') {
      return;
    }

    // Se já foi processado, não processar novamente
    if (prefillProcessedRef.current) {
      return;
    }

    // Primeiro, atualizar o ref se houver state disponível
    if (location.state?.prefillRecibo && !prefillReciboRef.current) {
      prefillReciboRef.current = location.state.prefillRecibo;
    }

    // Verificar se há prefill no ref ou no location.state
    const prefillData = prefillReciboRef.current || location.state?.prefillRecibo;
    
    if (!prefillData) {
      return;
    }

    if (!customers.length) {
      return; // Não marcar como processado, aguardar clientes
    }

    const selectedCustomer = customers.find((customerItem) => customerItem.idcustomer === prefillData.customerId);

    if (!selectedCustomer) {
      // Não marcar como processado, pode ser que os clientes ainda não estejam completamente carregados
      // ou o cliente realmente não existe - mas vamos tentar novamente quando os clientes mudarem
      return;
    }

    // Verificar se o cliente já está selecionado corretamente no estado
    const currentCustomer = getSelectedCustomer();
    if (currentCustomer?.idcustomer === prefillData.customerId) {
      // Cliente já está selecionado no estado, mas pode não ter os documentos carregados
      // Se não há documentos carregados, chamar handleCustomerSelection para carregá-los
      if (documentos.length === 0) {
        handleCustomerSelection(selectedCustomer, { prefillDocument: prefillData.document });
        // Marcar como processado após iniciar o carregamento
        prefillProcessedRef.current = true;
        prefillReciboRef.current = null;
      } else if (prefillData.document && !invoice.lines.some(line => line.doc === prefillData.document.doc)) {
        // Documentos já estão carregados, apenas pré-selecionar o documento
        preselectReceiptDocument(documentos, prefillData.document);
        // Marcar como processado após pré-selecionar
        prefillProcessedRef.current = true;
        prefillReciboRef.current = null;
      } else {
        // Documento já foi pré-selecionado, marcar como processado
        prefillProcessedRef.current = true;
        prefillReciboRef.current = null;
      }
    } else {
      // Cliente não está selecionado, chamar handleCustomerSelection para selecioná-lo
      handleCustomerSelection(selectedCustomer, { prefillDocument: prefillData.document });
      // Marcar como processado após iniciar a seleção
      prefillProcessedRef.current = true;
      prefillReciboRef.current = null;
    }

    // Limpar o state do location após processar
    if (history?.location && location.state?.prefillRecibo) {
      history.replace({
        pathname: history.location.pathname,
        search: history.location.search,
        hash: history.location.hash,
        state: { ...(history.location.state || {}), prefillRecibo: undefined },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, doctype, location.state]);

  const validateLine = (line, index) => {
    const errors = {};

    if (['recibos'].includes(doctype) && !line.doc) {
      errors[`documento-${index}`] = "Selecione um documento";
    }

    if (['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype) && !line.produto) {
      errors[`product-${index}`] = "Selecione um produto";
    }

    if (['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype) && (line.quantidade === '' || line.quantidade === null || line.quantidade === undefined || line.quantidade <= 0)) {
      errors[`quantidade-${index}`] = "A quantidade deve ser maior que zero";
    }

    // Validação de IVA = 'ND' (apenas para facturas e vendas_dinheiro, não para guias de transporte)
    if (['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) && line.produto && (line.iva === null || line.iva === undefined || line.iva === '')) {
      errors[`iva-${index}`] = "Taxa de IVA não encontrada";
    }

    const quantidade = line.quantidade === '' ? 0 : (line.quantidade || 0);
    const preco_sem_iva = line.preco_sem_iva === '' ? 0 : (line.preco_sem_iva || 0);
    const subtotal = quantidade * preco_sem_iva;
    const valor_desconto = line.valor_desconto === '' ? 0 : (line.valor_desconto || 0);
    if (['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype) && valor_desconto > subtotal) {
      errors[`discount-${index}`] =
        "O desconto não pode ser maior que o subtotal";
    }

    if (['recibos'].includes(doctype)) {
      if (line.tipo === 'notas_credito') {
        if (roundToTwo(line.valor_inicial) > -roundToTwo(line.valor)) {
          errors[`valor-${index}`] =
            "O valor não pode ser maior que o valor em divida." + `(${roundToTwo(line.valor_inicial)} > ${roundToTwo(line.valor)})`;
        }
      } else {
        if (roundToTwo(line.valor_inicial) < roundToTwo(line.valor)) {
          errors[`valor-${index}`] =
            "O valor não pode ser maior que o valor em divida." + `(${roundToTwo(line.valor_inicial)} < ${roundToTwo(line.valor)})`;
        }
      }
    }

    return errors;
  };

  const updateLine = (index, updates) => {
    setInvoice((prev) => {
      const newLines = [...prev.lines];
      const line = { ...newLines[index], ...updates };
      let totals = {};

      if (['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype)) {
        const quantidade = line.quantidade === '' ? 0 : (line.quantidade || 0);
        const preco_sem_iva = line.preco_sem_iva === '' ? 0 : (line.preco_sem_iva || 0);
        totals.subtotal = smartRound(quantidade * preco_sem_iva, 6);

        if (updates.hasOwnProperty("valor_desconto")) {
          const valor_desconto = updates.valor_desconto === '' ? 0 : Math.min(
            Math.max(updates.valor_desconto || 0, 0),
            totals.subtotal
          );
          const desconto_per =
          totals.subtotal > 0 ? (valor_desconto / totals.subtotal) * 100 : 0;
          line.valor_desconto = valor_desconto;
          // Só atualizar desconto_per se não estivermos editando esse campo
          if (focusedField !== `desconto_per-${index}`) {
            line.desconto_per = smartRound(desconto_per);
          }
        } else if (updates.hasOwnProperty("desconto_per")) {
          const desconto_per = updates.desconto_per === '' ? 0 : Math.min(
            Math.max(updates.desconto_per || 0, 0),
            100
          );
          const valor_desconto = (totals.subtotal * desconto_per) / 100;
          line.desconto_per = desconto_per;
          // Só atualizar valor_desconto se não estivermos editando esse campo
          if (focusedField !== `valor_desconto-${index}`) {
            line.valor_desconto = smartRound(valor_desconto);
          }
        }

        const afterDiscount = totals.subtotal - (line.valor_desconto || 0);
        totals.taxAmount = afterDiscount * ((line.taxa_iva || 0) / 100);
        totals.total = afterDiscount + totals.taxAmount;

        // Atualizar preco_com_iva apenas se não estivermos editando o campo PVP c/IVA
        if (focusedField !== `preco_com_iva-${index}` && updates.hasOwnProperty("preco_sem_iva")) {
          line.preco_com_iva = updates.preco_sem_iva === 0 ? 0 : valueWithIva(updates.preco_sem_iva, line.taxa_iva || 0);
        }

        // Proteger campos PVP quando estão sendo editados, mesmo com descontos
        if (focusedField === `preco_sem_iva-${index}` && updates.hasOwnProperty("preco_sem_iva")) {
          // Não recalcular preco_com_iva se estivermos editando preco_sem_iva
          // O valor será atualizado quando o campo perder o foco
        } else if (focusedField === `preco_com_iva-${index}` && updates.hasOwnProperty("preco_com_iva")) {
          // Não recalcular preco_sem_iva se estivermos editando preco_com_iva
          // O valor será atualizado quando o campo perder o foco
        }

        const lineErrors = validateLine(line, index);
        if (Object.keys(lineErrors).length > 0) {
          setValidationErrors((prev) => ({
            ...prev,
            lines: { ...prev.lines, ...lineErrors },
          }));
        }
      } else if (['recibos'].includes(doctype)) {
        totals.valor = line.valor;
        const valor_inicial = line.valor_inicial;
        const valor_pago = valor_inicial - totals.valor;
        const lineErrors = validateLine(line, index);
        if (Object.keys(lineErrors).length > 0) {
          setValidationErrors((prev) => ({
            ...prev,
            lines: { ...prev.lines, ...lineErrors },
          }));
        }
        totals.valor_pago = valor_pago;
        totals.texto = valor_pago === 0 ? "(PAGO)" : "(PARTE)";
      }

      newLines[index] = {
        ...line,
        ...totals,
      };

      setValidationErrors((prev) => ({
        ...prev,
        lines: Object.keys(prev.lines).reduce((acc, key) => {
          if (!key.includes(`-${index}`)) {
            acc[key] = prev.lines[key];
          }
          return acc;
        }, {}),
      }));

      return calculateTotals(prev, newLines);
    });
  };

  const handleProductChange = async (index, _, product, invoiceData) => {
    if (!product) {
      setValidationErrors((prev) => ({
        ...prev,
        lines: {
          ...prev.lines,
          [`product-${index}`]: "Selecione um produto válido",
        },
      }));
      return;
    }

    const produto = product.idproduct;
    const postoId = invoiceData.lines[index]?.postoId || (invoiceData.lines[index]?.postoId == 0 ? 0 : -1);
    const customerId = invoiceData?.customerId || 'CF';
    const quantidade = invoiceData.lines[index]?.quantidade || 1;
    try {
      const productData = await productsService.getById(profile.idEmpresa, produto, customerId);
      const productDetail = productData[0];
      const postoValue = productDetail.values.find(
        (v) => v.idposto == postoId
      );

      console.log('postoValue', postoValue);
      
      const iva = postoValue
        ? postoValue.iva
        : '';

      const matchingtaxa_iva = taxa_ivas.find((t) => t.code === iva?.toString()) || { tax: 0 };
      const taxa_iva = matchingtaxa_iva ? Number(matchingtaxa_iva.tax) : iva;

      const oldprice = postoValue
        ? Number(threeDecimal(valueWithoutIva(Number(postoValue.price), taxa_iva)))
        : Number(threeDecimal(valueWithoutIva(Number(productDetail.maxValues.price), taxa_iva)));
      const costprice = postoValue
        ? Number(postoValue.costprice)
        : Number(productDetail.maxValues.costprice);

      const best = calculateBestCondition(oldprice, costprice, productDetail.conditions);

      const price = best.unitPrice;
      const lineUpdates = {
        oldprice: oldprice,
        produto: productDetail.idproduct,
        designacao: productDetail.nome,
        isp: productDetail.isp,
        preco_sem_iva: price,
        preco_com_iva: valueWithIva(price, taxa_iva),
        iva: iva,
        taxa_iva: taxa_iva,
        obs_ft: ['guias_transporte'].includes(doctype) && productDetail.obs_guias ? productDetail.obs_guias : '',
      };
      if (best.desconto_per > 0) {
        lineUpdates.desconto_per = best.desconto_per;
      } else if (best.valor_desconto > 0) {
        lineUpdates.valor_desconto = smartRound(best.valor_desconto * quantidade);
      } else {
        lineUpdates.desconto_per = 0;
        lineUpdates.valor_desconto = 0;
      }

      setValidationErrors((prev) => ({
        ...prev,
        lines: Object.keys(prev.lines).reduce((acc, key) => {
          if (!key.includes(`-${index}`)) {
            acc[key] = prev.lines[key];
          }
          return acc;
        }, {}),
      }));

      updateLine(index, lineUpdates);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      setValidationErrors((prev) => ({
        ...prev,
        lines: {
          ...prev.lines,
          [`product-${index}`]: "Erro ao carregar detalhes do produto",
        },
      }));
    }
  };

  const isValidNif = () => {
    if (!invoice.nif) return true;
    if (invoice.customerId === 'CF') {
      return validateNIF(invoice.nif);
    }
    return true;
  }

  const validateInvoice = () => {
    const errors = {
      customer: !invoice.customerId,
      nif: !isValidNif(invoice.nif),
      date: !invoice.date,
      data_vencimento: !['recibos'].includes(doctype) && !invoice.data_vencimento,
      paymentMethod: !['notas_credito'].includes(doctype) && !invoice.paymentMethod,
      lines: {},
    };

    // Validação específica para guias de transporte
    if (['guias_transporte'].includes(doctype)) {
      // Verificar se login AT está preenchido
      errors.at_login = !isAtLoginValid || !atLogin || !atLogin.username;
      
      // Verificar se local de descarga tem valor (incluindo valor por defeito do cliente)
      const localDescargaValue = invoice.local_descarga || (getSelectedCustomer().address ? `${getSelectedCustomer().address}\n${getSelectedCustomer().postal_code}, ${getSelectedCustomer().district}` : '');
      const localCargaValue = invoice.local_carga || (isCustomCarga ? profile?.fullEmpresa?.address + '\n' + profile?.fullEmpresa?.postalcode + ', ' + profile?.fullEmpresa?.city : '');
      
      errors.local_carga = !localCargaValue || localCargaValue.trim() === '';
      errors.data_hora_carga = !invoice.data_hora_carga;
      errors.local_descarga = !localDescargaValue || localDescargaValue.trim() === '';
      
      // Validações de data
      if (invoice.data_hora_carga && invoice.date) {
        const dataGeral = new Date(invoice.date);
        const dataCarga = new Date(invoice.data_hora_carga);
        if (dataCarga < dataGeral) {
          errors.data_hora_carga = true;
        }
      }
      
      if (invoice.data_hora_carga && invoice.data_hora_descarga) {
        const dataCarga = new Date(invoice.data_hora_carga);
        const dataDescarga = new Date(invoice.data_hora_descarga);
        if (dataDescarga <= dataCarga) {
          errors.data_hora_descarga = true;
        }
      }
    }

    // Validação específica para recibos - pelo menos uma linha selecionada para liquidar
    if (['recibos'].includes(doctype)) {
      if (invoice.lines.length === 0) {
        return {
          ...errors,
          general: "Deve selecionar pelo menos uma linha para liquidar",
        };
      }
    } else if (invoice.lines.length === 0) {
      return {
        ...errors,
        general: "Deve adicionar pelo menos uma linha à fatura",
      };
    }

    invoice.lines.forEach((line, index) => {
      const lineErrors = validateLine(line, index);
      if (Object.keys(lineErrors).length > 0) {
        errors.lines = { ...errors.lines, ...lineErrors };
      }
    });

    return errors;
  };

  const validateGuiasTransporte = (errors) => {
    if (['guias_transporte'].includes(doctype)) {
      // Validação de datas específicas para guias de transporte
      if (invoice.date && invoice.data_hora_carga) {
        const dataGeral = new Date(invoice.date);
        const dataCarga = new Date(invoice.data_hora_carga);
        if (dataCarga < dataGeral) {
          errors.data_hora_carga = true;
        }
      }
      
      if (invoice.data_hora_carga && invoice.data_hora_descarga) {
        const dataCarga = new Date(invoice.data_hora_carga);
        const dataDescarga = new Date(invoice.data_hora_descarga);
        if (dataDescarga <= dataCarga) {
          errors.data_hora_descarga = true;
        }
      }
    }
    return errors;
  };

  const handleSubmit = (e, preview = false) => {    
    if (e) e.preventDefault();

    // Bloquear submissão se houver linhas com IVA = 'ND'
    if (hasLinesWithNDIva()) {
      NotificationManager.error('Corrija as linhas com IVA não encontrado antes de submeter', 'Erro de Validação', 3000);
      return;
    }

    setIsSubmiting(true);
    showGlobalLoading('A processar documento...');
    eventBus.$emit("Modal-document-print", { isOpen: false });

    const errors = validateInvoice();
    const finalErrors = validateGuiasTransporte(errors);
    setValidationErrors(finalErrors);

    console.error('errors', errors);

    if (
      finalErrors.general ||
      Object.keys(finalErrors.lines).length > 0 ||
      finalErrors.customer ||
      finalErrors.nif ||
      finalErrors.date ||
      finalErrors.data_vencimento ||
      finalErrors.paymentMethod ||
      finalErrors.at_login ||
      finalErrors.local_carga ||
      finalErrors.data_hora_carga ||
      finalErrors.local_descarga ||
      finalErrors.data_hora_descarga
    ) {
      hideGlobalLoading();
      setIsSubmiting(false);
      return;
    }

    setError(null);

    const totalWithoutVAT = invoice.subtotal - invoice.totalDiscount;
    const totalDiscounts = smartRound(invoice.lines.reduce(
      (acc, line) => acc + (smartRound(line.valor_desconto) || 0),
      0
    ));

    let payload;
    if (['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype)) {
      payload = {
        doctype: ['notas_credito'].includes(doctype) ? "notas_credito" : "invoices",
        idempresa: profile.idEmpresa,
        idcustomer: invoice.customerId,
        date_time: dateToTimestamp(invoice.date),
        data_vencimento: invoice.data_vencimento.toLocaleString('pt-PT', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).slice(0, 10),
        payment: invoice.paymentMethod,
        desc: totalDiscounts,
        nif: invoice.nif,
        iva: invoice.totalTax,
        total: invoice.total,
        total_sem_iva: totalWithoutVAT,
        obs: invoice.obs || '',
        preview: preview ? previewId : null,
        linhas: invoice.lines.map((line) => ({
          idproduct: line.produto,
          idposto: line.postoId,
          quantidade: line.quantidade,
          price: line.preco_sem_iva,
          taxa_iva: line.taxa_iva,
          iva: line.iva,
          desc: line.valor_desconto || 0,
          desc_per: line.desconto_per || 0,
          obs_ft: line.obs_ft || '',
        })),
      };
    } else if (['guias_transporte'].includes(doctype)) {
      // Separar data_hora_carga em data_carga e hora_carga
      const dataHoraCarga = invoice.data_hora_carga || '';
      let dataCarga = '';
      let horaCarga = '';
      
      if (dataHoraCarga) {
        const parts = dataHoraCarga.split('T');
        dataCarga = parts[0] || '';
        horaCarga = parts[1] || '';
        
        // Garantir que a hora tem formato HH:MM:SS
        if (horaCarga && horaCarga.includes(':')) {
          const horaParts = horaCarga.split(':');
          if (horaParts.length === 2) {
            horaCarga = `${horaParts[0].padStart(2, '0')}:${horaParts[1].padStart(2, '0')}`;
          } else if (horaParts.length === 3) {
            horaCarga = `${horaParts[0].padStart(2, '0')}:${horaParts[1].padStart(2, '0')}`;
          }
        }
      }
      
      // Separar data_hora_descarga em data_descarga e hora_descarga
      const dataHoraDescarga = invoice.data_hora_descarga || '';
      let dataDescarga = '';
      let horaDescarga = '';
      
      if (dataHoraDescarga) {
        const parts = dataHoraDescarga.split('T');
        dataDescarga = parts[0] || '';
        horaDescarga = parts[1] || '';
        
        // Garantir que a hora tem formato HH:MM:SS
        if (horaDescarga && horaDescarga.includes(':')) {
          const horaParts = horaDescarga.split(':');
          if (horaParts.length === 2) {
            horaDescarga = `${horaParts[0].padStart(2, '0')}:${horaParts[1].padStart(2, '0')}:`;
          } else if (horaParts.length === 3) {
            horaDescarga = `${horaParts[0].padStart(2, '0')}:${horaParts[1].padStart(2, '0')}`;
          }
        }
      }
      
      payload = {
        doctype: "guias_transporte",
        idempresa: profile.idEmpresa,
        idcustomer: invoice.customerId,
        date_time: dateToTimestamp(currentDateTime()),
        nif: invoice.nif,
        total: invoice.total,
        obs: invoice.obs || '',
        local_carga: invoice.local_carga || (isCustomCarga ? profile?.fullEmpresa?.address + '\n' + profile?.fullEmpresa?.postalcode + ', ' + profile?.fullEmpresa?.city : ''),
        data_carga: dataCarga,
        hora_carga: horaCarga,
        local_descarga: invoice.local_descarga || (getSelectedCustomer().address ? `${getSelectedCustomer().address}\n${getSelectedCustomer().postal_code}, ${getSelectedCustomer().district}` : ''),
        data_descarga: dataDescarga,
        hora_descarga: horaDescarga,
        matricula: invoice.matricula || '',
        motorista: invoice.motorista || '',
        preview: preview ? previewId : null,
        linhas: invoice.lines.map((line) => ({
          idproduct: line.produto,
          idposto: line.postoId,
          quantidade: line.quantidade,
          obs_ft: line.obs_ft || '',
        })),
      };
      
      console.log('Guias Transporte Payload:', {
        local_carga: invoice.local_carga,
        local_descarga: invoice.local_descarga,
        data_carga: dataCarga,
        hora_carga: horaCarga,
        data_descarga: dataDescarga,
        hora_descarga: horaDescarga,
        matricula: invoice.matricula
      });
    } else if (['recibos'].includes(doctype)) {
      payload = {
        doctype: "receipts",
        idempresa: profile.idEmpresa,
        idcustomer: invoice.customerId,
        date_time: dateToTimestamp(invoice.date),
        payment: invoice.paymentMethod,
        nif: invoice.nif,
        total: invoice.total,
        obs: invoice.obs || '',
        preview: preview ? previewId : null,
        linhas: invoice.lines.map((line) => ({
          numero_documento: line.numero_documento,
          date_time: line.date_time,
          data_documento: line.data_documento,
          tipo_documento: line.tipo_documento,
          valor_inicial: ['notas_credito'].includes(line.tipo) ? line.valor_inicial * -1 : line.valor_inicial,
          valor: line.valor,
          texto: line.diff == line.valor ? '(Pago)' : '(Parte)',
        })),
      };
      console.log(payload);
      
    }

    facturasService.set(payload)
      .then((response) => {
        // Verificar se há erro de comunicação AT para guias de transporte
        if (['guias_transporte'].includes(doctype) && response && response.at_communication) {
          // Verificar se success é false
          const hasErrors = response.at_communication.success === false;
          
          // Verificar se há códigos de erro diferentes de 0
          const responseStatus = response.at_communication.raw?.ResponseStatus;
          const hasErrorCodes = responseStatus && 
            (Array.isArray(responseStatus) 
              ? responseStatus.some(status => status.ReturnCode !== 0)
              : responseStatus.ReturnCode !== 0);
          
          if (hasErrors || hasErrorCodes) {
            let errorMessage = response.at_communication?.error || "Erro na comunicação com a AT";
            
            // Adicionar informações detalhadas da resposta da AT se disponíveis
            if (responseStatus) {
              const statuses = Array.isArray(responseStatus) ? responseStatus : [responseStatus];
              
              // Verificar se algum status tem código diferente de 0
              const errorStatuses = statuses.filter(status => status.ReturnCode !== 0);
              
              if (errorStatuses.length > 0) {
                errorMessage += "\n\nErros da AT:";
                errorStatuses.forEach(status => {
                  errorMessage += `\n• Código ${status.ReturnCode}: ${status.ReturnMessage}`;
                });
              }
            }
            
            // Adicionar URL e ambiente se disponíveis
            if (response.at_communication.url) {
              errorMessage += `\n\nURL: ${response.at_communication.url}`;
            }
            if (response.at_communication.environment) {
              errorMessage += `\nAmbiente: ${response.at_communication.environment}`;
            }
            
            NotificationManager.error(errorMessage, "Erro na Comunicação AT", 8000);
            setIsSubmiting(false);
            hideGlobalLoading();
            return;
          }
          
          // Mostrar aviso se ReturnCode = 0 mas há mensagem de alerta
          if (response.at_communication.success === true && responseStatus && !hasErrorCodes) {
            const status = Array.isArray(responseStatus) ? responseStatus[0] : responseStatus;
            if (status.ReturnMessage && status.ReturnMessage.toLowerCase().includes('alerta')) {
              NotificationManager.warning(
                status.ReturnMessage,
                "Aviso da AT",
                5000
              );
            }
          }
        }
        
        if (!['NOK', 'OLD'].includes(response)) {
          // Verificar se há aviso de comunicação AT mas documento foi criado
          if (['guias_transporte'].includes(doctype) && response.at_communication && response.at_communication.success === false) {
            NotificationManager.warning(
              `Guia criada localmente mas falhou comunicação com AT: ${response.at_communication.error}`,
              "Aviso - Comunicação AT",
              5000
            );
          }
          
          if (preview) {
            const { type, serie, numero } = response;
            if (selectedPageSize === 'a4') {
              setPreviewUrl(`https://eposgestofinal.com/api/facturas/pdf.php?idempresa=${profile.idEmpresa}&type=${type}&serie=${serie}&numero=${numero}&preview=${previewId}&date_time=${dateToTimestamp(invoice.date)}&page_type=${selectedPageType}`);
            } else {
              setPreviewUrl(`https://eposgestofinal.com/api/facturas/receipt.php?idempresa=${profile.idEmpresa}&type=${type}&serie=${serie}&numero=${numero}&preview=${previewId}&date_time=${dateToTimestamp(invoice.date)}&page_type=${selectedPageType}`);
            }
            
            // eslint-disable-next-line no-unused-expressions
            printerRef.current?.showPreview().then(() => {
              setIsSubmiting(false);
              hideGlobalLoading();
            });
          } else {
            setValidationErrors({
              lines: {},
              customer: false,
              nif: false,
              date: false,
              data_vencimento: false,
              paymentMethod: false,
            });
            setError(null);
            const { type, numero, serie, date_time } = response;
            
            // Mostrar notificação de sucesso para guias de transporte
            if (['guias_transporte'].includes(doctype)) {
              NotificationManager.success(`Guia ${serie}/${numero} criada com sucesso`, "Guia de Transporte", 3000);
            }
            
            setIsSubmiting(false);
            hideGlobalLoading();
            if (['recibos'].includes(doctype)) {
              setTimeout(() => history.push(`/sales/${type}/${date_time}/${serie}/${numero}`), 1000);
            } else if (['guias_transporte'].includes(doctype)) {
              setTimeout(() => history.push(`/sales`), 1000);
            } else {
              setTimeout(() => history.push(`/sales/${type}/${serie}/${numero}`), 1000);
            }
          }
        } else if (response === 'OLD') {
          setValidationErrors((prev) => ({
            ...prev,
            general: "Já existe um documento com data posterior para esta série.",
          }));
          setIsSubmiting(false);
          hideGlobalLoading();
        } else {
          setValidationErrors((prev) => ({
            ...prev,
            general: "Erro ao submeter documento. Por favor, tente novamente.",
          }));
          setIsSubmiting(false);
          hideGlobalLoading();
        }
      })
      .catch((err) => {
        console.error('Error submitting document:', err);
        let errorMessage = err?.response?.data?.error || err?.response?.data?.at_communication?.error || "Erro ao submeter documento. Por favor, tente novamente.";
        
        // Para guias de transporte, adicionar informações detalhadas da AT se disponíveis
        if (['guias_transporte'].includes(doctype) && err?.response?.data?.at_communication?.raw?.ResponseStatus) {
          const responseStatus = err.response.data.at_communication.raw.ResponseStatus;
          const statuses = Array.isArray(responseStatus) ? responseStatus : [responseStatus];
          
          // Verificar se algum status tem código diferente de 0
          const errorStatuses = statuses.filter(status => status.ReturnCode !== 0);
          
          if (errorStatuses.length > 0) {
            errorMessage += "\n\nErros da AT:";
            errorStatuses.forEach(status => {
              errorMessage += `\n• Código ${status.ReturnCode}: ${status.ReturnMessage}`;
            });
          }
          
          if (err.response.data.at_communication.url) {
            errorMessage += `\n\nURL: ${err.response.data.at_communication.url}`;
          }
          if (err.response.data.at_communication.environment) {
            errorMessage += `\nAmbiente: ${err.response.data.at_communication.environment}`;
          }
        }
        
        // Para guias de transporte, mostrar notificação
        if (['guias_transporte'].includes(doctype)) {
          NotificationManager.error(errorMessage, "Erro ao Submeter Documento", 8000);
        } else {
          setValidationErrors((prev) => ({
            ...prev,
            general: errorMessage,
          }));
        }
        
        setIsSubmiting(false);
        hideGlobalLoading();
      });
  };

  const handlePrint = () => {    
    const errors = validateInvoice();
    setValidationErrors(errors);

    console.error('errors', errors);

    if (
      errors.general ||
      Object.keys(errors.lines).length > 0 ||
      errors.customer ||
      errors.nif ||
      errors.date ||
      errors.data_vencimento ||
      errors.paymentMethod
    ) {
      setIsSubmiting(false);
      return;
    }

    setError(null);
    setIsSubmiting(true);

    handleSubmit(null, true);
  };

  const getSelectedPaymentMethod = () => {
    return (
      payments.find((p) => p.value === invoice.paymentMethod) || payments[0]
    );
  };

  const getRecorringFrequency = () => {
    return (
      frequences.find((p) => p.value === recurrence.frequency) || frequences.find((p) => p.value === 'monthly')
    );
  };

  const canAddProduct = invoice.customerId;

  const isLastLineFilled = () => {
    if (invoice.lines.length === 0) return true;
    const lastLine = invoice.lines[invoice.lines.length - 1];
    
    // Para guias de transporte, só verificar produto e quantidade
    if (['guias_transporte'].includes(doctype)) {
      const quantidade = parseFloat(lastLine.quantidade) || 0;
      const isValid = lastLine.produto !== "" && quantidade > 0;
      console.log('isLastLineFilled guias:', {
        produto: lastLine.produto,
        quantidade: lastLine.quantidade,
        quantidadeParsed: quantidade,
        isValid
      });
      return isValid;
    }
    
    // Para outros documentos, verificar produto, posto e quantidade
    return lastLine.produto !== "" && lastLine.postoId !== -1 && lastLine.quantidade > 0;
  };

  const isLastReceiptLineFilled = () => {
    if (invoice.lines.length === 0) return true;
    const lastLine = invoice.lines[invoice.lines.length - 1];
    return lastLine.numero_documento !== "" && lastLine.valor > 0;
  };


  const getSelectedCustomer = () => {
    return (
      customers.find((c) => c.idcustomer === invoice.customerId) ||
      defaultCustomer
    );
  };

  // Para guias de transporte, verificar se cabeçalho está completo
  const isHeaderValid = () => {
    if (!['guias_transporte'].includes(doctype)) return true;
    
    // 1. Verificar se login AT está preenchido
    console.log('atLogin', atLogin);
    if (!isAtLoginValid || !atLogin || !atLogin.username) {
      console.log('Header validation failed: AT Login not filled');
      return false;
    }
    
    // 2. Verificar se cliente está selecionado
    if (!invoice.customerId) {
      console.log('Header validation failed: No customer selected');
      return false;
    }
    
    // 3. Verificar se local de descarga tem valor (incluindo valor por defeito do cliente)
    const localDescargaValue = invoice.local_descarga || (getSelectedCustomer().address ? `${getSelectedCustomer().address}\n${getSelectedCustomer().postal_code}, ${getSelectedCustomer().district}` : '');
    if (!localDescargaValue || localDescargaValue.trim() === '') {
      console.log('Header validation failed: Empty discharge address');
      return false;
    }
    
    // 4. Verificar se local de carga tem valor (incluindo valor por defeito da empresa)
    const localCargaValue = invoice.local_carga || (isCustomCarga ? profile?.fullEmpresa?.address + '\n' + profile?.fullEmpresa?.postalcode + ', ' + profile?.fullEmpresa?.city : '');
    if (!localCargaValue || localCargaValue.trim() === '') {
      console.log('Header validation failed: Empty load address');
      return false;
    }
    
    // 5. Verificar se data e hora de carga estão preenchidas
    if (!invoice.data_hora_carga) {
      console.log('Header validation failed: Empty load date/time');
      return false;
    }
    
    // 6. Verificar se matrícula está selecionada
    if (!invoice.matricula) {
      console.log('Header validation failed: Empty matricula');
      return false;
    }
    
    // 7. Verificar se data de carga não é anterior à data geral
    const dataGeral = new Date(invoice.date);
    const dataCarga = new Date(invoice.data_hora_carga);
    if (dataCarga < dataGeral) {
      console.log('Header validation failed: Load date is before general date');
      return false;
    }
    
    // 8. Verificar se data de descarga não é anterior ou igual à data de carga
    const dataDescarga = new Date(invoice.data_hora_descarga);
    if (dataDescarga <= dataCarga) {
      console.log('Header validation failed: Discharge date is before or equal to load date');
      return false;
    }
    
    console.log('Header validation: All checks passed');
    return true;
  };

  // Definir canAddProduct após isHeaderValid estar disponível
  const canAddProductGuias = () => {
    return ['guias_transporte'].includes(doctype) ? isHeaderValid() : invoice.customerId;
  };

  // Verificar se pode submeter o formulário
  const canSubmit = () => {
    // Bloquear se houver linhas com IVA = 'ND' (apenas para facturas e vendas_dinheiro)
    if (hasLinesWithNDIva()) {
      return false;
    }
    return true;
  };

  // Atualizar canAddNewLine com a função correta
  const canAddNewLineGuias = () => {
    if (['recibos'].includes(doctype)) {
      // Para recibos, verificar se pelo menos uma linha está selecionada para liquidar
      return invoice.customerId && invoice.lines.length > 0;
    } else if (['guias_transporte'].includes(doctype)) {
      // Para guias de transporte, verificar se cabeçalho está válido (não validar IVA)
      const headerValid = canAddProductGuias();
      const lastLineValid = isLastLineFilled();
      console.log('Guias validation:', {
        doctype,
        headerValid,
        lastLineValid,
        linesLength: invoice.lines.length,
        lastLine: invoice.lines[invoice.lines.length - 1]
      });
      return headerValid && lastLineValid;
    } else {
      // Para facturas e vendas_dinheiro, bloquear se houver linhas com IVA = 'ND'
      if (hasLinesWithNDIva()) {
        return false;
      }
      // Para outros documentos, comportamento normal
      return invoice.customerId && (['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) ? isLastLineFilled() : isLastReceiptLineFilled());
    }
  };

  if (['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) && invoice.customerId && !invoice.lines.length) {
    addLine(true);
  }
  
  if (['guias_transporte'].includes(doctype) && isHeaderValid() && !invoice.lines.length) {
    addLine(true);
  }

  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: "100%" }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: "left",
              marginTop: "75px",
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li>
              <a href="/sales">Faturação</a>
            </li>
            <li className="active">
              <span>Novo Documento</span>
            </li>
          </ol>
        </div>
      </div>
      <TestEnvironmentAlert />
      <div className="fullContainer" style={{ marginTop: "25px" }}>
        <div
          className="col-sm-12"
          style={{ textAlign: "center", fontSize: "10px" }}
        >
          <div className="panel panel-default">
            <div className="container-fluid">
              <div className="invoice-panel">
                <div className="header">
                  <h1 className="invoice-title">{doctypesTitles(doctype)}</h1>
                  {['facturas', 'vendas_dinheiro'].includes(doctype) ? (<div className="flex items-center space-x-4" style={{ display: 'flex', gap: '10px', color: 'rgb(107, 114, 128)', flexDirection: 'row', justifyContent: 'flex-end' }}>
                      <MdRefresh size={20} className="text-gray-500" />
                      <span style={{ fontSize: '12pt', fontWeight: 'bolder'}}>Fatura Recorrente</span>
                      <Switch
                        name={"recurrence|isRecurring"}
                        checked={recurrence.isRecurring}
                        handleChange={(e) => {
                          const newRecurring = e?.target?.checked || false;                  
                          setRecurrence((prev) => {                            
                            return {
                              ...prev,
                              isRecurring: newRecurring,
                            }
                          });
                        }}
                      />
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={recurrence.isRecurring}
                        onChange={(e) => setRecurrence({...recurrence, isRecurring: e.target.checked})}
                      />
                  </div>) : null}
                </div>

                <form onSubmit={handleSubmit}>
                  {validationErrors.general && (
                    <Alert severity="error" className="mb-4">
                      {validationErrors.general}
                    </Alert>
                  )}

                  {['guias_transporte'].includes(doctype) && (
                    <>
                      {/* Subgrupo de Login AT */}
                      <div className="panel panel-default" style={{ marginTop: '20px' }}>
                        <div className="panel-heading">
                          <h3 className="panel-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                            🔐 Login AT (Obrigatório)
                          </h3>
                        </div>
                        <div className="panel-body">
                          <AtLogin
                            onLogin={(loginData) => {
                              setAtLogin(loginData);
                              setIsAtLoginValid(true);
                            }}
                            selectedEmpresas={false}
                          />
                          {validationErrors.at_login && (
                            <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px', color: '#dc3545' }}>
                              O login AT é obrigatório para guias de transporte
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '20px', marginBottom: '20px' }}>
                  {recurrence.isRecurring ? (
                      <div style={{ marginTop: '20px' }}>
                        <h1 className="title">Recorrência</h1>
                        <div className="form-grid" style={{ margin: 0 }}>
                          <div className="form-group">
                            <label className="form-label">Frequência</label>
                            <AutoCompleteField
                              options={frequences}
                              value={getRecorringFrequency()}
                              onChange={(_, frequency) => {
                                setRecurrence((prev) => {                            
                                  return {
                                    ...prev,
                                    frequency: frequency.value,
                                  }
                                });
                              }}
                              getOptionLabel={(option) => option.label}
                              label="Selecione a recorrência"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Intervalo</label>
                            <TextField
                                type="number"
                                min="1"
                                label="Intervalo"
                                value={recurrence.interval || ''}
                                onChange={(e) => {
                                  const newInterval = e?.target?.value || '';
                                  setRecurrence((prev) => {                            
                                    return {
                                      ...prev,
                                      interval: newInterval,
                                    }
                                  });
                                }}
                              />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Data Inicial</label>
                            <Input
                              inputType="date"
                              name="date"
                              value={recurrence.startDate}
                              handleChange={(e) => {
                                const newDate = e?.target?.value || '';
                                setRecurrence((prev) => {                            
                                  return {
                                    ...prev,
                                    startDate: newDate,
                                  }
                                });
                              }}
                              className={validationErrors.date ? "error" : ""}
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Com Data Final</label>
                            <Switch
                              name={"recurrence|withEndDate"}
                              checked={recurrence.withEndDate}
                              handleChange={(e) => {
                                const newwithEndDate = e?.target?.checked || false;                  
                                setRecurrence((prev) => {                            
                                  return {
                                    ...prev,
                                    withEndDate: newwithEndDate,
                                  }
                                });
                              }}
                            />
                          </div>

                          {recurrence.withEndDate ? (
                            <div className="form-group">
                              <label className="form-label">Data Final</label>
                              <Input
                                inputType="date"
                                name="date"
                                value={recurrence.endDate}
                                handleChange={(e) => {
                                  const newDate = e?.target?.value || '';
                                  setRecurrence((prev) => {                            
                                    return {
                                      ...prev,
                                      endDate: newDate,
                                    }
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    {recurrence.isRecurring ? (<h1 className="title">Cabeçalho</h1>) : null}
                    <div className="form-grid" style={{ margin: 0 }}>
                      <div className="form-group">
                        <label className="form-label">Cliente</label>
                        <AutoCompleteField
                          ref={
                            ['facturas', 'recibos'].includes(doctype) && !invoice.customerId
                              ? latestCustomerRef
                              : null
                          }
                          options={customers.filter(({inactive}) => !(inactive === 't'))}
                          value={getSelectedCustomer()}
                          onChange={(_, customer) => handleCustomerSelection(customer)}
                          getOptionLabel={(option) => option.name || ""}
                          searchableFields={['name', 'nif', 'email', 'phone', 'mobile', 'fax']}
                          label="Selecione um cliente"
                          required
                          error={validationErrors.customer}
                          helperText={
                            validationErrors.customer
                              ? "Selecione um cliente"
                              : ""
                          }
                        />
                      </div>

                      {['recibos'].includes(doctype) ? (<div className="form-group">
                        <label className="form-label">NIF</label>
                        <TextField
                          type="text"
                          label="NIF"
                          value={getSelectedCustomer().nif}
                          readOnly
                        />
                      </div>) : null}

                      {['vendas_dinheiro'].includes(doctype) && invoice.customerId === 'CF' ? (<div className="form-group">
                        <label className="form-label">NIF</label>
                        <TextField
                          type="text"
                          label="NIF"
                          value={invoice.nif}
                          onChange={(e) => {
                            const newNif = e?.target?.value || '';
                            setValidationErrors((prev) => ({
                              ...prev,
                              nif: false,
                            }));
                            setInvoice((prev) => ({
                              ...prev,
                              nif: newNif,
                            }));
                          }}
                          error={validationErrors.nif}
                          helperText={
                            validationErrors.nif
                              ? "NIF inválido"
                              : ""
                          }
                        />
                      </div>) : null}

                      {['vendas_dinheiro', 'recibos'].includes(doctype) ? (<div className="form-group">
                        <label className="form-label">Método de Pagamento</label>
                        <AutoCompleteField
                          options={payments}
                          value={getSelectedPaymentMethod()}
                          onChange={(_, payment) => {
                            setValidationErrors((prev) => ({
                              ...prev,
                              paymentMethod: false,
                            }));
                            setInvoice((prev) => ({
                              ...prev,
                              paymentMethod: payment ? payment.value : 1,
                            }));
                          }}
                          getOptionLabel={(option) => option.label}
                          label="Selecione o método de pagamento"
                          required
                          error={validationErrors.paymentMethod}
                          helperText={
                            validationErrors.paymentMethod
                              ? "Selecione um método de pagamento"
                              : ""
                          }
                        />
                      </div>) : null}

                      <div className="form-group">
                        <label className="form-label">Série</label>
                        <TextField
                          type="text"
                          label="Série"
                          value={series[mapSeriesDoctype(doctype)]}
                          readOnly
                        />
                      </div>

                      {!recurrence.isRecurring ? (
                        <>
                          {['facturas'].includes(doctype) ? (<div className="form-group">
                            <label className="form-label">Vencimento</label>
                            <Vencimento
                              value={invoice.data_vencimento}
                              onChange={(newDate) => setInvoice((prev) => ({
                                ...prev,
                                data_vencimento: newDate,
                              }))}
                            />
                          </div>) : null}

                          {!['guias_transporte'].includes(doctype) ? (<div className="form-group">
                            <label className="form-label">{['guias_transporte'].includes(doctype) ? 'Data' : 'Data e Hora'}</label>
                            <Input
                              inputType={['guias_transporte'].includes(doctype) ? 'date' : 'datetime-local'}
                              name="date"
                              value={invoice.date}
                              handleChange={(e) => {
                                const newDate = e?.target?.value || '';
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  date: false,
                                }));                          
                                setInvoice((prev) => {                                  
                                  return {
                                    ...prev,
                                    date: newDate,
                                  }
                                });
                              }}
                              className={validationErrors.date ? "error" : ""}
                            />
                          </div>) : null}
                        </>
                      ) : null}

                      <div className="form-group">
                        <label className="form-label">Observações</label>
                        <TextField
                          type="text"
                          label="Observações do documento"
                          value={headerObs}
                          onChange={(e) => {
                            const newValue = e?.target?.value || '';
                            setHeaderObs(newValue);
                            setInvoice((prev) => ({
                              ...prev,
                              obs: newValue,
                            }));
                          }}
                          placeholder="Digite observações para o documento (opcional)"
                        />
                      </div>
                    </div>

                    {['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) && invoice.customerId && invoice.customerId !== 'CF' && (
                      <div className="customer-details-panel panel">
                        <div className="panel-heading">
                          <h3 className="panel-title">
                            <MdBusiness size={16} className="icon-left" />
                            Detalhes do Cliente
                          </h3>
                        </div>
                        <div className="panel-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdPerson size={14} className="icon-left" />
                                  Nome
                                </div>
                                <div className="detail-value">{getSelectedCustomer().name}</div>
                              </div>
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdFingerprint size={14} className="icon-left" />
                                  NIF
                                </div>
                                <div className="detail-value">{getSelectedCustomer().nif}</div>
                              </div>
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdPhone size={14} className="icon-left" />
                                  Telefone
                                </div>
                                <div className="detail-value">{getSelectedCustomer().phone}</div>
                              </div>
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdPhone size={14} className="icon-left" />
                                  Telemovel
                                </div>
                                <div className="detail-value">{getSelectedCustomer().mobile}</div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdEmail size={14} className="icon-left" />
                                  Email
                                </div>
                                <div className="detail-value">{getSelectedCustomer().email}</div>
                              </div>
                              <div className="detail-group">
                                <div className="detail-label">
                                  <MdLocationOn size={14} className="icon-left" />
                                  Morada
                                </div>
                                <div className="detail-value">
                                  <p>{getSelectedCustomer().address}</p>
                                  <p>{getSelectedCustomer().codigo_postal} {getSelectedCustomer().morada_2}</p>
                                </div>
                              </div>
                              <div className="detail-group total-invoiced">
                                <div className="detail-label">
                                  <MdReceipt size={14} className="icon-left" />
                                  Plafond
                                </div>
                                <div className="detail-value">
                                  {getSelectedCustomer().plafond > 0 ? getSelectedCustomer().plafond : '-'} €
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {['guias_transporte'].includes(doctype) ? (
                  <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '20px', marginBottom: '20px' }}>
                    <h2 className="products-title">Informação de Transporte</h2>
                    <div className="form-grid" style={{ margin: 0 }}>
                        <>
                          {/* Subgrupo de Veículo */}
                          <div className="panel panel-default" style={{ marginTop: '20px' }}>
                            <div className="panel-heading">
                              <h3 className="panel-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                                <i className="fas fa-truck" style={{ marginRight: '8px' }}></i> Veículo
                              </h3>
                            </div>
                            <div className="panel-body">
                              {/* Campo Matrícula */}
                              <div className="form-group">
                                <label className="form-label">Matrícula *</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1.5 }}>
                                    <AutoCompleteField
                                      options={[
                                        { id: 'custom', matricula: 'Customizada' },
                                        ...matriculas.map(mat => ({ id: mat.codigo, matricula: mat.matricula }))
                                      ]}
                                      value={selectedMatricula}
                                      onChange={(_, matricula) => {
                                        if (matricula) {
                                          setSelectedMatricula(matricula);
                                          setIsCustomMatricula(matricula.id === 'custom');
                                          
                                          if (matricula.id === 'custom') {
                                            setInvoice((prev) => ({
                                              ...prev,
                                              matricula: '',
                                            }));
                                          } else {
                                            setInvoice((prev) => ({
                                              ...prev,
                                              matricula: matricula.matricula,
                                            }));
                                          }
                                        }
                                      }}
                                      getOptionLabel={(option) => option.matricula || ""}
                                      label="Selecione uma matrícula"
                                      placeholder="Escolha uma matrícula"
                                    />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <TextField
                                      type="text"
                                      label="Matrícula"
                                      value={invoice.matricula || ''}
                                      onChange={(e) => {
                                        const newValue = e?.target?.value || '';
                                        setInvoice((prev) => ({
                                          ...prev,
                                          matricula: newValue,
                                        }));
                                      }}
                                      placeholder={isCustomMatricula ? "Digite a matrícula" : "Selecione uma matrícula acima"}
                                      disabled={!isCustomMatricula}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="form-group">
                                <label className="form-label">Motorista (opcional)</label>
                                <TextField
                                  type="text"
                                  label="Nome do motorista"
                                  value={invoice.motorista || ''}
                                  onChange={(e) => {
                                    const newValue = e?.target?.value || '';
                                    setInvoice((prev) => ({
                                      ...prev,
                                      motorista: newValue,
                                    }));
                                  }}
                                  placeholder="Digite o nome do motorista"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Subgrupo de Carga */}
                          <div className="panel panel-default" style={{ marginTop: '20px' }}>
                            <div className="panel-heading">
                              <h3 className="panel-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                                <i className="fas fa-truck-loading" style={{ marginRight: '8px' }}></i> Dados de Carga
                              </h3>
                            </div>
                            <div className="panel-body">
                              <div className="form-group">
                                <label className="form-label">Data e Hora de Carga *</label>
                                <Input
                                  inputType="datetime-local"
                                  name="data_hora_carga"
                                  value={invoice.data_hora_carga}
                                  handleChange={(e) => {
                                    const newValue = e?.target?.value || '';
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      data_hora_carga: false,
                                    }));
                                    setInvoice((prev) => ({
                                      ...prev,
                                      data_hora_carga: newValue,
                                    }));
                                  }}
                                  className={validationErrors.data_hora_carga ? "error" : ""}
                                />
                                {validationErrors.data_hora_carga && (
                                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                                    A data de carga não pode ser anterior à data geral
                                  </div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label">Local de Carga *</label>
                                <div style={{ marginBottom: '10px' }}>
                                  <AutoCompleteField
                                    options={[
                                      { id: 'custom', nome: 'Customizada', address: '', postalcode: '', city: '' },
                                      ...postos.filter(posto => posto.posto !== '0')
                                    ]}
                                    value={selectedPostoCarga}
                                    onChange={(_, posto) => {
                                      if (posto) {
                                        if (posto.id === 'custom') {
                                          setSelectedPostoCarga(posto);
                                          setIsCustomCarga(true);
                                          setValidationErrors((prev) => ({
                                            ...prev,
                                            local_carga: false,
                                          }));
                                          setInvoice((prev) => ({
                                            ...prev,
                                            local_carga: '',
                                          }));
                                        } else {
                                          setSelectedPostoCarga(posto);
                                          setIsCustomCarga(false);
                                          setValidationErrors((prev) => ({
                                            ...prev,
                                            local_carga: false,
                                          }));

                                          // Formatar address: [addresss] na primeira linha, [postalcode], [city] na segunda
                                          let formattedAddress = '';
                                          if (posto.address) {
                                            formattedAddress = posto.address;
                                          }
                                          if (posto.postalcode && posto.city) {
                                            if (formattedAddress) {
                                              formattedAddress += '\n' + posto.postalcode + ', ' + posto.city;
                                            } else {
                                              formattedAddress = posto.postalcode + ', ' + posto.city;
                                            }
                                          }
                                          
                                          setInvoice((prev) => ({
                                            ...prev,
                                            local_carga: formattedAddress,
                                          }));
                                        }
                                      }
                                    }}
                                    getOptionLabel={(option) => option.nome || ""}
                                    label="Selecione um posto ou customizada"
                                    placeholder="Escolha um posto ou opção customizada"
                                  />
                                </div>
                                <TextArea
                                  name="local_carga"
                                  value={invoice.local_carga || (isCustomCarga ? profile?.fullEmpresa?.address + '\n' + profile?.fullEmpresa?.postalcode + ', ' + profile?.fullEmpresa?.city : '')}
                                  handleChange={(e) => {
                                    const newValue = e?.target?.value || '';
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      local_carga: false,
                                    }));
                                    setInvoice((prev) => ({
                                      ...prev,
                                      local_carga: newValue,
                                    }));
                                  }}
                                  placeholder={isCustomCarga ? "Digite o local de carga" : "Selecione um posto acima ou escolha 'Customizada'"}
                                  rows={3}
                                  disabled={!isCustomCarga}
                                />
                                {validationErrors.local_carga && (
                                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                                    O local de carga é obrigatório
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Subgrupo de Descarga */}
                          <div className="panel panel-default" style={{ marginTop: '20px' }}>
                            <div className="panel-heading">
                              <h3 className="panel-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                                <i className="fas fa-box-open" style={{ marginRight: '8px' }}></i> Dados de Descarga
                              </h3>
                            </div>
                            <div className="panel-body">
                              <div className="form-group">
                                <label className="form-label">Data e Hora de Descarga</label>
                                <Input
                                  inputType="datetime-local"
                                  name="data_hora_descarga"
                                  value={invoice.data_hora_descarga}
                                  handleChange={(e) => {
                                    const newValue = e?.target?.value || '';
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      data_hora_descarga: false,
                                    }));
                                    setInvoice((prev) => ({
                                      ...prev,
                                      data_hora_descarga: newValue,
                                    }));
                                  }}
                                  className={validationErrors.data_hora_descarga ? "error" : ""}
                                />
                                {validationErrors.data_hora_descarga && (
                                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                                    A data de descarga deve ser posterior à data de carga
                                  </div>
                                )}
                              </div>

                              <div className="form-group">
                                <TextArea
                                  title="Local de Descarga *"
                                  name="local_descarga"
                                  value={invoice.local_descarga || (getSelectedCustomer().address ? `${getSelectedCustomer().address}\n${getSelectedCustomer().postal_code}, ${getSelectedCustomer().district}` : '')}
                                  handleChange={(e) => {
                                    const newValue = e?.target?.value || '';
                                    setValidationErrors((prev) => ({
                                      ...prev,
                                      local_descarga: false,
                                    }));
                                    setInvoice((prev) => ({
                                      ...prev,
                                      local_descarga: newValue,
                                    }));
                                  }}
                                  placeholder="Digite o local de descarga"
                                  rows={3}
                                />
                                {validationErrors.local_descarga && (
                                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                                    O local de descarga é obrigatório
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      </div>

                  </div>
                  ) : null}
                  <div className="products-section">
                    {['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype) ? (
                      <>
                        <h2 className="products-title">Produtos</h2>
                      </>
                    ) : (
                      <h2 className="products-title">Linhas</h2>
                    )}

                    {['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) ? invoice.lines.map((line, index) => (
                      <div key={index} className="product-grid">
                        <div className="product-field">
                            <label style={{ marginBottom: '-2px' }} className="form-label">Posto *</label>
                            <AutoCompleteField
                              ref={index === invoice.lines.length - 1 ? latestPostoRef : null}
                              options={postos}
                              value={postos.find((p) => Number(p.posto) === Number(line.postoId))}
                              onChange={async (_, posto) => {
                                if (posto) {
                                  const postoId = Number(posto.posto);
                                  await setInvoice(prev => {
                                    const newLines = [...prev.lines];
                                    newLines[index] = {
                                      ...newLines[index],
                                      postoId
                                    };
                                    if (line.produto) {
                                      handleProductChange(index, null, products.find(p => p.idproduct === line.produto), { ...prev, lines: newLines });
                                    }
                                    return {
                                      ...prev,
                                      lines: newLines
                                    };
                                  });
                                  
                                  setTimeout(() => index === invoice.lines.length - 1 && latestProductRef.current?.focus(), 100);
                                }
                              }}
                              getOptionLabel={(option) => option.nome || ""}
                              label="Selecione um posto"
                              error={Boolean(validationErrors.lines[`posto-${index}`])}
                              helperText={validationErrors.lines[`posto-${index}`]}
                            />
                          </div>
                        <div className="product-field">
                        <label style={{ marginBottom: '-2px' }} className="form-label">Produto *</label>
                          <AutoCompleteField
                            ref={
                              index === invoice.lines.length - 1
                                ? latestProductRef
                                : null
                            }
                            options={products.filter(({inactive}) => !(inactive === 't'))}
                            searchableFields={['nome', 'barcodes', 'idproduct']}
                            value={
                              products.find(
                                (p) => p.idproduct === line.produto
                              ) || null
                            }
                            onChange={(_, product) => {
                              if (product) {
                                setInvoice(prev => {
                                  const newLines = [...prev.lines];
                                  newLines[index] = {
                                    ...newLines[index],
                                    produto: product.idproduct,
                                    designacao: product.nome
                                  };
                                  handleProductChange(index, null, product, { ...prev, lines: newLines});
                                  return {
                                    ...prev,
                                    lines: newLines
                                  };
                                });
                              }
                            }}
                            getOptionLabel={(option) => option.nome || ""}
                            label="Selecione um produto"
                            error={Boolean(
                              validationErrors.lines[`product-${index}`]
                            )}
                            helperText={
                              validationErrors.lines[`product-${index}`]
                            }
                          />
                        </div>
                        <div className="input-field observation-field">
                          <TextField
                            type="text"
                            label="Obs"
                            value={line.obs_ft || ''}
                            onChange={(e) => {
                              updateLine(index, {
                                obs_ft: e?.target?.value || '',
                              });
                            }}
                            placeholder=""
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0.001"
                            label="Quantidade"
                            value={line.quantidade || ''}
                            onChange={(e) => {
                              const newQuantidade = e?.target?.value || '';
                              updateLine(index, {
                                quantidade: newQuantidade,
                              });
                            }}
                            error={Boolean(
                              validationErrors.lines[`quantidade-${index}`]
                            )}
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0"
                            step="0.001"
                            label="PVP s/iva"
                            {...createFieldHandlers(index, 'preco_sem_iva', line)}
                          />
                          {line.oldprice > (line.preco_sem_iva || 0) ? (
                            <span style={{ position: "relative", display: "inline-block", color: "red" }}>
                              {(line.oldprice || 0).toFixed(2)} €
                            </span>
                          ) : null}
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0"
                            step="0.001"
                            label="PVP c/iva"
                            value={
                              focusedField === `preco_com_iva-${index}` 
                                ? (tempValues[`preco_com_iva-${index}`] !== undefined ? tempValues[`preco_com_iva-${index}`] : '')
                                : line.preco_sem_iva ? valueWithIva(line.preco_sem_iva, line.taxa_iva || 0) : ''
                            }
                            {...createFieldHandlers(index, 'preco_com_iva', line)}
                          />
                          {line.oldprice > (line.preco_sem_iva || 0) ? (
                            <span style={{ position: "relative", display: "inline-block", color: "red" }}>
                              {valueWithIva((line.oldprice || 0), line.taxa_iva || 0).toFixed(2)} €
                            </span>
                          ) : null}
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            label="Desc %"
                            {...createFieldHandlers(index, 'desconto_per', line)}
                            error={Boolean(
                              validationErrors.lines[`discount-${index}`]
                            )}
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0"
                            step="0.001"
                            label="Desc €"
                            value={
                              focusedField === `valor_desconto-${index}` 
                                ? (tempValues[`valor_desconto-${index}`] !== undefined ? tempValues[`valor_desconto-${index}`] : '')
                                : (line.valor_desconto === 0 ? '' : (line.valor_desconto !== undefined && line.valor_desconto !== null ? line.valor_desconto : ''))
                            }
                            {...createFieldHandlers(index, 'valor_desconto', line)}
                            error={Boolean(
                              validationErrors.lines[`discount-${index}`]
                            )}
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="text"
                            label="IVA"
                            value={
                              line.iva === null || line.iva === undefined
                                ? "ND"
                                : line.taxa_iva === 0
                                ? "0%"
                                : `${line.taxa_iva}%`
                            }
                            readOnly
                            error={Boolean(validationErrors.lines[`iva-${index}`])}
                            helperText={validationErrors.lines[`iva-${index}`]}
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="text"
                            label="Total"
                            value={(line.total || 0).toFixed(2) + " €"}
                            readOnly
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          className="delete-button"
                        >
                          <MdClear size={28} />
                        </button>
                      </div>
                    )) : null}

                    {/* Linhas simplificadas para guias de transporte - apenas produto e quantidade */}
                    {['guias_transporte'].includes(doctype) ? invoice.lines.map((line, index) => (
                      <div key={index} className="product-grid guias-simplified">
                        <div className="product-field">
                        <label style={{ marginBottom: '-2px' }} className="form-label">Produto *</label>
                          <AutoCompleteField
                            ref={
                              index === invoice.lines.length - 1
                                ? latestProductRef
                                : null
                            }
                            options={products.filter(({inactive}) => !(inactive === 't'))}
                            searchableFields={['nome', 'barcodes', 'idproduct']}
                            value={
                              products.find(
                                (p) => p.idproduct === line.produto
                              ) || null
                            }
                            onChange={(_, product) => {
                              if (product) {
                                setInvoice(prev => {
                                  const newLines = [...prev.lines];
                                  newLines[index] = {
                                    ...newLines[index],
                                    produto: product.idproduct,
                                    designacao: product.nome
                                  };
                                  handleProductChange(index, null, product, { ...prev, lines: newLines});
                                  return {
                                    ...prev,
                                    lines: newLines
                                  };
                                });
                              }
                            }}
                            getOptionLabel={(option) => option.nome || ""}
                            label="Selecione um produto"
                            error={Boolean(
                              validationErrors.lines[`product-${index}`]
                            )}
                            helperText={
                              validationErrors.lines[`product-${index}`]
                            }
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="number"
                            min="0"
                            step="0.001"
                            label="Quantidade"
                            value={line.quantidade || ''}
                            onChange={(e) => {
                              const newQuantidade = e?.target?.value || '';
                              updateLine(index, {
                                quantidade: newQuantidade,
                              });
                            }}
                            error={Boolean(
                              validationErrors.lines[`quantidade-${index}`]
                            )}
                          />
                        </div>

                        <div className="input-field">
                          <TextField
                            type="text"
                            label="Obs"
                            value={line.obs_ft || ''}
                            onChange={(e) => {
                              updateLine(index, {
                                obs_ft: e?.target?.value || '',
                              });
                            }}
                            placeholder=""
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          className="delete-button"
                        >
                          <MdClear size={28} />
                        </button>
                      </div>
                    )) : null}

                    {['recibos'].includes(doctype) ? documentos.map((documento, index) => (
                      <div key={index} className="product-grid receipt-grid">
                        <div className="product-field">
                          <TextField
                            type="text"
                            label="Documento"
                            value={documento.doc}
                            readOnly
                          />
                        </div>

                        <div className="product-field">
                          <TextField
                            type="text"
                            label="Data"
                            value={documento.data}
                            readOnly
                          />
                        </div>

                        <div className="product-field">
                          <TextField
                            type="text"
                            label="Valor em Falta"
                            value={documento.diff}
                            readOnly
                          />
                        </div>

                        <div className="input-field">
                          <span>Liquidar</span>
                          <div className="switch-with-field">
                            <div className="switch-wrapper">
                              <Switch
                                checked={invoice.lines.find((l) => l.doc == documento.doc)}
                                handleChange={(e) => {
                                  const enable = e?.target?.checked || false;
                                  if (enable) {
                                      const doc = documento.doc;
                                      const diff = Number(documento.diff);
                                      const numero_documento = Number(documento.numero);
                                      const date_time = Number(documento.date_time);
                                      const data_documento = documento.data;
                                      const tipo = documento.tipo;
                                      const tipo_documento = ['facturas'].includes(documento.tipo) ? 'FT' : ['notas_credito'].includes(documento.tipo) ? 'NC' : 'FA';
                                      const valor_inicial = roundToTwo(documento.valor_liquido);
                                      const valor = roundToTwo(documento.diff);
                                      setInvoice(prev => {
                                        const newLines = [...prev.lines];
                                        newLines.push({
                                          ...emptyLineRecibos,
                                          doc,
                                          numero_documento,
                                          date_time,
                                          data_documento,
                                          tipo_documento,
                                          tipo,
                                          valor_inicial,
                                          valor,
                                          diff,
                                        });
                                        return calculateTotals(prev, newLines);
                                      });
                                  } else {
                                    setInvoice(prev => {
                                      const newLines = prev.lines.filter((l) => l.doc !== documento.doc);
                                      return calculateTotals(prev, newLines);
                                    });
                                  }
                                }}
                              />
                            </div>
                            {invoice.lines.find((l) => l.doc == documento.doc) && (
                              <div className="valor-liquidar-field">
                                <TextField
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  max={documento.diff}
                                  label="Valor a Liquidar"
                                  value={invoice.lines.find((l) => l.doc == documento.doc).valor || ''}
                                  onChange={(e) => {
                                    const diff = invoice.lines.find((l) => l.doc == documento.doc).diff;
                                    let newQuantidade = e?.target?.value || '';
                                    if (newQuantidade > diff) {
                                      newQuantidade = diff;
                                    }
                                    const index = invoice.lines.findIndex((l) => l.doc == documento.doc);
                                    updateLine(index, {
                                      valor: newQuantidade,
                                    });
                                  }}
                                  error={Boolean(validationErrors.lines[`valor-${index}`])}
                                  helperText={validationErrors.lines[`valor-${index}`]}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )) : null}

                    {['facturas', 'vendas_dinheiro', 'notas_credito', 'guias_transporte'].includes(doctype) ? (
                      <div style={{ textAlign: "right", marginTop: "2rem" }}>
                        <button
                          type="button"
                          onClick={() => addLine(!invoice.lines.length)}
                          disabled={!canAddNewLineGuias()}
                          className="add-product-button"
                          title={
                            !canAddProductGuias()
                              ? (['guias_transporte'].includes(doctype) ? "Preencha primeiro o cabeçalho completo" : "Selecione um cliente primeiro")
                              : ['recibos'].includes(doctype)
                              ? (invoice.lines.length === 0 ? "Selecione pelo menos uma linha para liquidar" : "Adicionar novo produto")
                              : !isLastLineFilled()
                              ? "Preencha a última linha antes de adicionar uma nova"
                              : "Adicionar novo produto"
                          }
                        >
                          <MdAddCircleOutline size={24} />
                          Adicionar Produto
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {!['guias_transporte'].includes(doctype) && (
                    <div className="totals">
                      <div style={{ 
                        marginBottom: '2rem', 
                        fontSize: '1.5rem', 
                        fontWeight: '700', 
                        color: '#1e293b',
                        textAlign: 'center',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase'
                      }}>
                        Resumo Financeiro
                      </div>
                      {['facturas', 'vendas_dinheiro', 'notas_credito'].includes(doctype) ? (
                        <>
                          <div className="total-row">
                            <span className="total-label">Subtotal</span>
                            <span className="total-value">{(invoice?.subtotal || 0).toFixed(2)} €</span>
                          </div>
                          {invoice?.totalDiscount > 0 ? (
                            <div className="total-row">
                              <span className="total-label">Descontos</span>
                              <span className="total-value">{(invoice?.totalDiscount || 0).toFixed(2)} €</span>
                            </div>
                          ) : null}
                          {invoice?.ivas && Object.keys(invoice.ivas).length > 0 ? (
                            Object.keys(invoice.ivas).map(taxKey => (
                              invoice.ivas[taxKey] > 0 && (
                                <div key={taxKey} className="total-row">
                                  <span className="total-label">IVA ({taxKey})</span>
                                  <span className="total-value">{(invoice.ivas[taxKey] || 0).toFixed(2)} €</span>
                                </div>
                              )
                            ))
                          ) : (
                            <div className="total-row">
                              <span className="total-label">IVA</span>
                              <span className="total-value">{(invoice?.totalTax || 0).toFixed(2)} €</span>
                            </div>
                          )}
                        </>
                      ) : null}
                      <div className="total-row total-final">
                        <span className="total-label">Total</span>
                        <span className="total-value">{(invoice?.total || 0).toFixed(2)} €</span>
                      </div>
                    </div>
                  )}

                  <div className="form-actions-buttons">
                    <button
                      type="button"
                      onClick={() => history.push('/sales')}
                      className="back-button"
                    >
                      <MdTurnLeft size={28} />&nbsp;&nbsp;Voltar
                    </button>
                    <button type="button" disabled={!canAddNewLineGuias() || !canSubmit()} onClick={() => eventBus.$emit("Modal-document-print", { header: null, body: null })} className="preview-button">
                      { isSubmiting ? <ClipLoader size={20} /> : <MdPreview size={28} /> }&nbsp;&nbsp;Pré-vizualizar
                    </button>
                    {recurrence.isRecurring ? (
                      <button type="button" disabled={true} onClick={() => {}} className="submit-button">
                        { isSubmiting ? <ClipLoader size={20} /> : <MdAddCircleOutline size={28} /> }&nbsp;&nbsp;Criar Recorrência (Brevemente)
                      </button>
                    ) : (
                      <button type="button" disabled={!canAddNewLineGuias() || !canSubmit()} onClick={handleSubmit} className="submit-button">
                        { isSubmiting ? <ClipLoader size={20} /> : <MdAddCircleOutline size={28} /> }&nbsp;&nbsp;Submeter Documento
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PDFPrint ref={printerRef} pdfUrl={previewUrl} />

      <Modal id="document-print" modifier="very-small">
        <div class="select-page-to-print-modal">
          <div className="form-group" style={{ marginBottom: '20px', marginTop: '20px', marginLeft: '22px' }}>
            <RadioGroup
              column
              value={selectedPageType}
              onChange={(event) => setSelectedPageType(event.target.value)}
            >
              <FormControlLabel value="1" control={<Radio size="medium" />} style={{ border: '3px solid ' + (selectedPageType === '1' ? '#1A76D2' : '#E0E0E0'), borderRadius: '20px', padding: '10px' }} label={
                <div class="page-item">
                  <span class="main">Original</span>
                </div>
              } />
              <FormControlLabel value="2" control={<Radio size="medium" />} style={{ border: '3px solid ' + (selectedPageType === '2' ? '#1A76D2' : '#E0E0E0'), borderRadius: '20px', padding: '10px' }} label={
                <div class="page-item">
                  <span class="main">Duplicado</span>
                </div>
              } />
            </RadioGroup>
          </div>
          <div className="form-group" style={{ marginBottom: '20px', marginTop: '20px', marginLeft: '22px' }}>
            <h2>Tamanho de Impressão</h2>
            <RadioGroup
              column
              value={selectedPageSize}
              onChange={(event) => setSelectedPageSize(event.target.value)}
            >
              <FormControlLabel value="a4" control={<Radio size="medium" />} style={{ border: '3px solid ' + (selectedPageSize === 'a4' ? '#1A76D2' : '#E0E0E0'), borderRadius: '20px', padding: '10px' }} label={
                <div class="page-item">
                  <span class="main">A4</span>
                  <span class="secondary">Documento padrão</span>
                </div>
              } />
              <FormControlLabel value="80mm" control={<Radio size="medium" />} style={{ border: '3px solid ' + (selectedPageSize === '80mm' ? '#1A76D2' : '#E0E0E0'), borderRadius: '20px', padding: '10px' }} label={
                <div class="page-item">
                  <span class="main">80mm</span>
                  <span class="secondary">Impressoras POS/Recibos</span>
                </div>
              } />
            </RadioGroup>
          </div>
          <button className="btn btn-block btn-view-pdf" onClick={handlePrint}>
            Avançar
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default App;